<script lang="ts" setup>
import { computed, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import type { MessageAttachmentDTO } from '@nora-monorepo/nora-api-client';
import { TextPromptModal } from '@nora-monorepo/ui-shared';

import { useFilesModalStore } from '#nora-chat-ui/components/modals/files-modal/files-modal-store';
import { useAttachmentApiStore } from '#nora-chat-ui/stores/api/attachment-api-store';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';

interface EditFileNameProps {
  attachment?: MessageAttachmentDTO | null;
}

const props = defineProps<EditFileNameProps>();
const emits = defineEmits(['close', 'update']);

const apiStore = useAttachmentApiStore();
const modalStore = useFilesModalStore();
const conversationStore = useConversationStore();
const { t } = useI18n();

const { attachment } = toRefs(props);
const isSaving = ref(false);

function separateExtension(filename: string): { filename: string; extension?: string } {
  const lastDotIndex = filename.lastIndexOf('.');

  if (lastDotIndex === -1) {
    return { filename };
  }

  const name = filename.substring(0, lastDotIndex);
  const extension = filename.substring(lastDotIndex);

  return { filename: name, extension };
}

const inputValue = computed(() => {
  return attachment.value
    ? separateExtension(attachment.value.fileName).filename
    : '';
});

const extension = computed(() => {
  return attachment.value
    ? separateExtension(attachment.value.fileName).extension
    : '';
});

async function save(newName: string) {
  if (!attachment.value?.message?.conversationId) {
    throw new Error('Tried to update file name without a conversation ID.');
  }

  if (!attachment.value?.id) {
    throw new Error('Tried to update file name without a attachment ID.');
  }

  isSaving.value = true;

  const newNameWithExtension = `${newName}${extension.value}`;
  emits('update', newNameWithExtension);

  const response = await apiStore.editAttachmentFilename(
    newNameWithExtension,
    attachment.value,
    attachment.value?.message?.conversationId,
  );

  if (response && response.data) {
    conversationStore.updateAttachmentName(
      attachment.value?.message?.conversationId,
      attachment.value.messageId ?? '',
      attachment.value.id,
      newNameWithExtension,
    );
    modalStore.updateAttachmentName(attachment.value.id, newNameWithExtension);
  }
  isSaving.value = false;
}

function onClose() {
  emits('close');
}
</script>

<template>
  <TextPromptModal
    :model-value="!!attachment"
    :initial-text="inputValue"
    :title="t('attachments.editFileName')"
    :text-field-label="t('attachments.editFileNameLabel')"
    :placeholder="t('attachments.enterFileName')"
    :ok-label="t('common.save')"
    :cancel-label="t('common.cancel')"
    @save="save"
    @cancel="onClose"
  />
</template>
